import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Accessible Language Selector
 * 
 * Features:
 * - aria-expanded and aria-haspopup for screen readers
 * - Keyboard navigation (Enter/Space to open, Arrow keys to navigate, Escape to close)
 * - Focus management
 * - Screen reader announcements for language changes
 */

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
];

export default function LanguageSelector({ variant = 'default', className = '' }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const announcerRef = useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => (prev + 1) % languages.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => (prev - 1 + languages.length) % languages.length);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusedIndex >= 0) {
          selectLanguage(languages[focusedIndex].code);
        }
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const selectLanguage = (langCode) => {
    const lang = languages.find(l => l.code === langCode);
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    buttonRef.current?.focus();
    
    // Announce language change to screen readers
    if (announcerRef.current && lang) {
      announcerRef.current.textContent = `Language changed to ${lang.name}`;
    }
  };

  // Focus management for menu items
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuRef.current) {
      const items = menuRef.current.querySelectorAll('[role="menuitem"]');
      items[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Screen reader announcer */}
      <div
        ref={announcerRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />

      {/* Trigger button */}
      <Button
        ref={buttonRef}
        variant={variant === 'ghost' ? 'ghost' : 'outline'}
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls="language-menu"
        aria-label={`Select language. Current: ${currentLang.name}`}
        className="gap-1.5"
        data-testid="language-selector-button"
      >
        <Globe className="h-4 w-4" aria-hidden="true" />
        <span className="uppercase font-medium">{currentLang.code}</span>
        <ChevronDown 
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          aria-hidden="true" 
        />
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          id="language-menu"
          role="menu"
          aria-label="Select language"
          className="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-stone-100 shadow-lg z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200"
          onKeyDown={handleKeyDown}
        >
          <div className="py-1">
            {languages.map((lang, index) => (
              <button
                key={lang.code}
                role="menuitem"
                tabIndex={focusedIndex === index ? 0 : -1}
                onClick={() => selectLanguage(lang.code)}
                className={`w-full px-4 py-2.5 text-left flex items-center justify-between transition-colors ${
                  i18n.language === lang.code 
                    ? 'bg-primary/10 text-primary' 
                    : 'hover:bg-stone-50 text-stone-700'
                } ${focusedIndex === index ? 'bg-stone-100' : ''}`}
                aria-current={i18n.language === lang.code ? 'true' : undefined}
                data-testid={`language-option-${lang.code}`}
              >
                <div>
                  <div className="font-medium">{lang.nativeName}</div>
                  <div className="text-xs text-muted-foreground">{lang.name}</div>
                </div>
                {i18n.language === lang.code && (
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
